import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { waitForJob } from '@/api/jobs';
import { useSession } from '@/app/session';
import { Icon, Pill, useToast, type IconName, type Tone } from '@/design-system';
import { cx } from '@/lib/cx';
import { errorText } from '@/shell/errorText';
import { LoadError } from '@/shell/LoadError';
import {
  byProvider,
  connectIntegration,
  integrationsKey,
  integrationsQuery,
  maskedId,
  PROVIDERS,
  refreshIntegrations,
  type IntegrationItem,
  type IntegrationsOut,
  type IntegrationState,
  type Provider,
} from './api';
import s from './integrations.module.css';

const ICON: Record<Provider, IconName> = { payme: 'credit-card', click: 'smartphone', bts: 'truck', icangroup: 'boxes' };

/** The state pill (design_logic.js `intSt`), plus «Available» in the accent palette (P6-B02); unknown is muted. */
const PILL: Record<IntegrationState | 'unknown', Tone> = {
  available: 'acc',
  not_connected: 'mute',
  pending_verification: 'warn',
  connected: 'ok',
  disabled: 'mute',
  error: 'err',
  unknown: 'mute',
};

/** The one action per state: start onboarding (primary), retry it, or ask Laravel for the state. */
type Action = 'connect' | 'configure' | 'status' | 'retry';
function actionOf(state: IntegrationState | null): Action {
  if (state === 'available' || state === 'not_connected' || state === null) return 'connect';
  if (state === 'pending_verification') return 'status';
  if (state === 'error') return 'retry';
  return 'configure';
}

/**
 * /organizations/:orgId/integrations — «Интеграции» (DESIGN_SPEC §4.3, screen 06): the yellow banner and
 * the four provider cards. Every state is the store backend's (P6-B01 mirrors it); «Подключить» only asks
 * Laravel to start merchant onboarding, and a secret is never shown — the masked label at most.
 */
export function IntegrationsTab() {
  const { orgId = '' } = useParams();
  const { t } = useTranslation('integrations');
  const q = useQuery(integrationsQuery(orgId));
  if (q.isError) return <LoadError error={q.error} onRetry={() => void q.refetch()} />;
  const rows = q.data ? byProvider(q.data.items) : null;
  return (
    <>
      <div className={s.banner} role="note">
        <Icon name="shield-alert" size={16} />
        {t('banner')}
      </div>
      {rows && (
        <div className={s.grid}>
          {PROVIDERS.map((p) => (
            <ProviderCard key={p} orgId={orgId} row={rows[p]} hasStore={!!q.data?.store} />
          ))}
        </div>
      )}
    </>
  );
}

function ProviderCard({ orgId, row, hasStore }: { orgId: string; row: IntegrationItem; hasStore: boolean }) {
  const { t } = useTranslation('integrations');
  const { t: tc } = useTranslation('common');
  const { can } = useSession();
  const toast = useToast();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const name = t(`providers.${row.provider}.name`);
  const action = actionOf(row.state);
  const writes = action === 'connect' || action === 'retry';
  // C10: without the scope (or with nothing to start) the button keeps its look and says why.
  const reason = writes ? (!can('integration:connect') ? t('forbidden') : !hasStore ? t('no_store') : !row.can_connect ? t('cannot_connect') : null) : null;
  const masked = maskedId(row.display);
  const refresh = () => qc.invalidateQueries({ queryKey: integrationsKey(orgId) });
  const afterJob = (jobId: string) =>
    waitForJob(jobId)
      .catch(() => undefined)
      .finally(() => void refresh());

  const run = () => {
    if (busy) return;
    if (reason) {
      toast(reason);
      return;
    }
    // «Настроить»: the store's page where the client's people manage the provider, when the backend gave one.
    if (action === 'configure' && row.onboarding_url) {
      window.open(row.onboarding_url, '_blank', 'noopener,noreferrer');
      return;
    }
    setBusy(true);
    const call = writes
      ? connectIntegration(orgId, row.provider).then((r) => {
          qc.setQueryData<IntegrationsOut>(integrationsKey(orgId), (d) => (d ? { ...d, items: d.items.map((x) => (x.provider === row.provider ? r.integration : x)) } : d));
          toast(action === 'connect' ? t('toast.connect', { name }) : t('toast.status', { name }));
          void afterJob(r.job_id);
        })
      : refreshIntegrations(orgId).then((r) => {
          toast(t('toast.status', { name }));
          void afterJob(r.job_id);
        });
    call.catch((e: unknown) => toast(errorText(e, tc))).finally(() => setBusy(false));
  };

  const state = row.state ?? 'unknown';
  const why = row.last_error ? (tc as unknown as (k: string, o: Record<string, unknown>) => string)(`errors.${row.last_error}`, { defaultValue: row.last_error }) : undefined;
  return (
    <section className={s.card} aria-labelledby={`int-${row.provider}`}>
      <div className={s.top}>
        <div className={s.tile}>
          <Icon name={ICON[row.provider]} size={20} />
        </div>
        <Pill tone={PILL[state]} title={why}>
          {t(`state.${state}`)}
        </Pill>
      </div>
      <div className={s.text}>
        <span id={`int-${row.provider}`} className={s.name}>
          {name}
        </span>
        <span className={s.desc}>{t(`providers.${row.provider}.desc`)}</span>
      </div>
      <span className={s.secret}>{masked ?? tc('dash')}</span>
      <button
        type="button"
        className={cx(s.action, action === 'connect' ? s.primary : s.secondary)}
        onClick={run}
        aria-busy={busy}
        aria-disabled={!!reason || undefined}
        title={reason ?? undefined}
      >
        {t(`action.${action}`)}
      </button>
    </section>
  );
}
